import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  XCircle,
  AlertTriangle,
  Clock,
  X,
  ExternalLink,
  AlertCircle,
  Snowflake,
  Ban,
} from "lucide-react"

const StatusModalCheck = ({ status, onDismiss, contactUrl = "/support" }) => {
  const [dismissed, setDismissed] = useState(false)

  if (status === "active" || dismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    if (onDismiss) {
      setTimeout(() => {
        onDismiss()
      }, 300)
    }
  }

  const getAlertConfig = () => {
    switch (status) {
      case "pending":
        return {
          icon: Clock,
          title: "Account Pending Approval",
          message:
            "Your account is currently under review. You will have full access once approved.",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
          borderColor: "border-yellow-100 dark:border-yellow-800/30",
          borderLeft: "border-l-4 border-l-yellow-500",
          iconColor: "text-yellow-500 dark:text-yellow-400",
          iconBgColor: "bg-yellow-100 dark:bg-yellow-800/40",
          textColor: "text-yellow-800 dark:text-yellow-300",
          actionBg:
            "bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700",
          actionText: "Check Status",
        }

      case "inactive":
        return {
          icon: AlertCircle,
          title: "Account Inactive",
          message:
            "Your account is currently inactive. Please complete your profile or contact support.",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          borderColor: "border-blue-100 dark:border-blue-800/30",
          borderLeft: "border-l-4 border-l-blue-500",
          iconColor: "text-blue-500 dark:text-blue-400",
          iconBgColor: "bg-blue-100 dark:bg-blue-800/40",
          textColor: "text-blue-800 dark:text-blue-300",
          actionBg:
            "bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700",
          actionText: "Contact Support",
        }

      case "suspended":
        return {
          icon: XCircle,
          title: "Account Suspended",
          message:
            "Your account has been suspended. Please contact support for more information.",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          borderColor: "border-red-100 dark:border-red-800/30",
          borderLeft: "border-l-4 border-l-red-500",
          iconColor: "text-red-500 dark:text-red-400",
          iconBgColor: "bg-red-100 dark:bg-red-800/40",
          textColor: "text-red-800 dark:text-red-300",
          actionBg:
            "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700",
          actionText: "Appeal Suspension",
        }

      // ✅ NEW
      case "frozen":
        return {
          icon: Snowflake,
          title: "Account Frozen",
          message:
            "Your account is temporarily frozen. Please contact support to restore access.",
          bgColor: "bg-orange-50 dark:bg-orange-900/20",
          borderColor: "border-orange-100 dark:border-orange-800/30",
          borderLeft: "border-l-4 border-l-orange-500",
          iconColor: "text-orange-600 dark:text-orange-400",
          iconBgColor: "bg-orange-100 dark:bg-orange-800/40",
          textColor: "text-orange-800 dark:text-orange-300",
          actionBg:
            "bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700",
          actionText: "Contact Support",
        }

      // ✅ NEW
      case "blacklisted":
        return {
          icon: Ban,
          title: "Account Blacklisted",
          message:
            "Your account has been blacklisted. Please contact support for assistance.",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          borderColor: "border-red-100 dark:border-red-800/30",
          borderLeft: "border-l-4 border-l-red-700",
          iconColor: "text-red-700 dark:text-red-300",
          iconBgColor: "bg-red-200 dark:bg-red-900/40",
          textColor: "text-red-800 dark:text-red-200",
          actionBg:
            "bg-red-700 hover:bg-red-800 dark:bg-red-800 dark:hover:bg-red-900",
          actionText: "Contact Support",
        }

      default:
        return {
          icon: AlertTriangle,
          title: "Account Status Issue",
          message:
            "There appears to be an issue with your account status. Please contact support.",
          bgColor: "bg-amber-50 dark:bg-amber-900/20",
          borderColor: "border-amber-100 dark:border-amber-800/30",
          borderLeft: "border-l-4 border-l-amber-500",
          iconColor: "text-amber-500 dark:text-amber-400",
          iconBgColor: "bg-amber-100 dark:bg-amber-800/40",
          textColor: "text-amber-800 dark:text-amber-300",
          actionBg:
            "bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-700",
          actionText: "Contact Support",
        }
    }
  }

  const config = getAlertConfig()
  const Icon = config.icon

  return (
    <AnimatePresence>
      <div className="px-7 pt-2">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={`w-full border rounded-lg shadow-sm overflow-hidden  ${config.bgColor} ${config.borderColor} ${config.borderLeft}`}
        >
          <div className="p-3">
            <div className="flex items-start">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className={`flex-shrink-0 w-10 h-10 rounded-full ${config.iconBgColor} flex items-center justify-center`}
              >
                <Icon className={`h-5 w-5 ${config.iconColor}`} />
              </motion.div>

              <div className="ml-3 justify-start flex-col items-start flex-1">
                <div className="flex justify-between items-start">
                  <motion.h3
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className={`text-base font-medium ${config.textColor}`}
                  >
                    {config.title}
                  </motion.h3>

                  <button
                    type="button"
                    className={`ml-3 flex-shrink-0 p-1 rounded-full ${config.textColor} hover:bg-white/20 dark:hover:bg-black/20 transition-colors`}
                    onClick={handleDismiss}
                  >
                    <span className="sr-only">Dismiss</span>
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  className={`mt-1 text-start text-xs ${config.textColor} opacity-90`}
                >
                  {config.message}
                </motion.p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default StatusModalCheck
